import React, { useEffect, useId, useRef, useState } from 'react';
import FilledNavigationGlyph from './FilledNavigationGlyph';
import { ReactComponent as MenuIcon } from '@/assets/navigation/compact/list.svg';
import { ReactComponent as CloseIcon } from '@/assets/navigation/compact/x-lg.svg';
import { ReactComponent as CheckIcon } from '@/assets/navigation/compact/check-lg.svg';
import { ReactComponent as ProfileIcon } from '@/assets/navigation/compact/person-fill.svg';
import { ReactComponent as FileIcon } from '@/assets/navigation/compact/file-earmark-text-fill.svg';
import { ReactComponent as ShieldIcon } from '@/assets/navigation/compact/shield-fill-check.svg';
import { ReactComponent as PreferencesIcon } from '@/assets/navigation/compact/gear-fill.svg';
import './ResponsivePageNavigation.css';

const ICONS = { account: ProfileIcon, file: FileIcon, documents: ShieldIcon, preferences: PreferencesIcon, 'employee-records': FileIcon };
function PageIcon({ item }) {
 const Icon = ICONS[item.id];
 return Icon ? <Icon className="vessel-page-menu__icon" aria-hidden="true" focusable="false"/> : <FilledNavigationGlyph name={item.glyph || 'workspace'} size={20}/>;
}

/** Collapses only the caller's existing page destinations; desktop DOM stays intact. */
export default function ResponsivePageNavigation({ items, label, current, secondary, footer, children, compactAt = 900 }) {
 const query = `(max-width: ${compactAt}px)`;
 const [compact, setCompact] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches);
 const [expanded, setExpanded] = useState(false);
 const trigger = useRef(null), dialog = useRef(null), desktop = useRef(null);
 const restoreDesktop = useRef(false);
 const id = useId();
 const pages = items.filter(item => !item.hidden);
 const selected = pages.find(item => item.id === current || item.selected) || pages[0];
 useEffect(() => {
  const media = window.matchMedia?.(query);
  if (!media) return undefined;
  const update = () => {
   if (!media.matches && dialog.current?.open) {restoreDesktop.current = true;dialog.current.close();}
   setCompact(media.matches);
  };
  update();media.addEventListener?.('change', update);
  return () => media.removeEventListener?.('change', update);
 }, [query]);
 useEffect(() => {
  if (!compact && restoreDesktop.current) {restoreDesktop.current=false;desktop.current?.querySelector('[aria-current="page"]')?.focus();}
 }, [compact]);
 useEffect(() => {
  if(dialog.current?.open){dialog.current.close();setExpanded(false);trigger.current?.focus({preventScroll:true});}
 }, [selected?.id]);
 const close = () => {dialog.current?.close();setExpanded(false);trigger.current?.focus({preventScroll:true});};
 const open = () => {
  const box=trigger.current.getBoundingClientRect(),width=Math.min(360,window.innerWidth-32);
  const top=Math.max(16,Math.min(box.bottom+10,window.innerHeight-108-160));
  Object.assign(dialog.current.style,{left:`${Math.max(16,Math.min(box.left,window.innerWidth-width-16))}px`,top:`${top}px`,width:`${width}px`,maxHeight:`${Math.max(160,window.innerHeight-top-108)}px`});
  dialog.current.showModal();setExpanded(true);
  dialog.current.querySelector('[aria-current="page"]')?.focus();
 };
 const follow = (item,event) => {
  if (item.disabled) {event.preventDefault();return;}
  if (item.to && (event.button!==0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)) return;
  if (item.onSelect) {event.preventDefault();close();item.onSelect(event);} else close();
 };
 if (!compact || !selected) return React.isValidElement(children) ? React.cloneElement(children,{ref:desktop}) : children;
 return <>
  <div className="vessel-page-navigation__compact" data-vessel-page-navigation>
   <button ref={trigger} className="vessel-page-navigation__trigger" type="button" aria-label={`${selected.label}. Open ${label}`} aria-haspopup="dialog" aria-controls={id} aria-expanded={expanded} onClick={open}>
    <PageIcon item={selected}/><span>{selected.label}</span><MenuIcon className="vessel-page-menu__icon vessel-page-navigation__menu-symbol" aria-hidden="true" focusable="false"/>
   </button>
   <dialog ref={dialog} id={id} className="vessel-page-menu" aria-labelledby={`${id}-title`} onCancel={event=>{event.preventDefault();close();}} onClose={()=>setExpanded(false)} onClick={event=>{if(event.target===event.currentTarget){const r=event.currentTarget.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)close();}}}>
    <header><h2 id={`${id}-title`}>{label}</h2><button type="button" className="vessel-page-menu__close" aria-label={`Close ${label}`} onClick={close}><CloseIcon aria-hidden="true" focusable="false"/></button></header>
    <nav aria-label={label}>{pages.map(item=>{
     const props={key:item.id,'aria-label':item.label,'aria-current':item.id===selected.id?'page':undefined,'aria-disabled':item.disabled||undefined,onClick:event=>follow(item,event)};
     const content=<><PageIcon item={item}/><span>{item.label}{item.detail&&<small>{item.detail}</small>}</span>{item.id===selected.id&&<CheckIcon className="vessel-page-menu__check" aria-hidden="true" focusable="false"/>}</>;
     return item.to ? <a {...props} href={item.to} tabIndex={item.disabled?-1:undefined}>{content}</a> : <button {...props} type="button" disabled={item.disabled}>{content}</button>;
    })}</nav>
    {footer&&<div className="vessel-page-menu__footer">{footer}</div>}
   </dialog>
  </div>
  {secondary&&<div className="vessel-page-navigation__secondary">{secondary}</div>}
 </>;
}
